// frontend/packages/business-console/src/services/blogAuthoringService.js
//
// Client for the AI-guided blog authoring agent
// (backend: /api/v2/blog-authoring). Mirrors workflowFormService's
// streaming author: EventSource can't send an Authorization header (F.1),
// so we use fetch + a ReadableStream reader with the same auth/tenant
// headers axios attaches.

import { getApiBase, buildTenantAuthHeaders } from '@upryzze/services';

/** Parse one SSE block ("event: x\ndata: {...}") into { event, data }. */
function parseSseBlock(block) {
  let event = 'message';
  const dataLines = [];
  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue; // comment / heartbeat
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  let data = {};
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    data = {};
  }
  return { event, data };
}

/**
 * Streaming author turn. Surfaces the chat reply as it's written and a
 * coarse "drafting" heartbeat while the (large) body streams.
 *
 * @param {object} input
 * @param {Array<{role:'user'|'assistant',content:string}>} input.messages
 * @param {object} [input.siteProfile]
 * @param {Array<{title:string,excerpt:string}>} [input.voiceExemplars]
 * @param {string[]} [input.businessFacts]
 * @param {{frontmatter?:object,body?:string}} [input.currentPost]
 * @param {object} [cb]
 * @param {(text:string)=>void} [cb.onMessage] cumulative chat-reply snapshot
 * @param {(phase:'building'|'repairing')=>void} [cb.onPhase]
 * @param {(chars:number)=>void} [cb.onProgress] body chars so far (heartbeat)
 * @param {AbortSignal} [cb.signal]
 * @returns {Promise<object>} the final `result` payload (message, post, ...).
 *   Throws an Error with `.response = { status, data }` on pre-stream HTTP
 *   error or an in-stream `error` event.
 */
export async function composeBlogPostStream(
  { messages, siteProfile, voiceExemplars, businessFacts, currentPost },
  { onMessage, onPhase, onProgress, signal } = {},
) {
  const path = '/v2/blog-authoring/compose/stream';
  const res = await fetch(`${getApiBase()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...buildTenantAuthHeaders(path),
    },
    body: JSON.stringify({
      messages,
      ...(siteProfile ? { siteProfile } : {}),
      ...(voiceExemplars && voiceExemplars.length ? { voiceExemplars } : {}),
      ...(businessFacts && businessFacts.length ? { businessFacts } : {}),
      ...(currentPost ? { currentPost } : {}),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    const err = new Error(data.message || `Request failed (${res.status})`);
    err.response = { status: res.status, data };
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let finalResult = null;
  let streamError = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, nl);
      buf = buf.slice(nl + 2);
      const evt = parseSseBlock(block);
      if (!evt) continue;
      if (evt.event === 'message') onMessage?.(evt.data.text || '');
      else if (evt.event === 'phase') onPhase?.(evt.data.phase);
      else if (evt.event === 'progress') onProgress?.(evt.data.chars || 0);
      else if (evt.event === 'result') finalResult = evt.data;
      else if (evt.event === 'error') streamError = evt.data;
    }
  }

  if (streamError) {
    const err = new Error(
      streamError.message || 'The AI writer hit a snag. Please retry.',
    );
    err.response = { status: streamError.status || 500, data: streamError };
    throw err;
  }
  if (!finalResult) {
    throw new Error('The AI writer did not return a result. Please retry.');
  }
  return finalResult;
}
